import { z } from "zod";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { hc } from "hono/client";
import {
  OrganizationRoutes,
  createOrganizationSchema,
  putOrganizationSchema,
} from "server/organization";
import type { S3Routes } from "server/s3";
import { authHeaders } from "@/utils/withAuth";

// ----------------------------------------------------------------
// Client Setup
// ----------------------------------------------------------------

/**
 * Client for Hono routes at /v1/super-admin/organization
 * (See apps/server/src/services/organization/organization.route.ts)
 */
const organizationClient = hc<OrganizationRoutes>(
  import.meta.env.VITE_SERVER_URL + "v1/super-admin/organization",
);

// ----------------------------------------------------------------
// Types
// ----------------------------------------------------------------

/**
 * Type representing the return value of fetchOrganizations function
 */
export type Organization = Awaited<
  ReturnType<typeof fetchOrganizations>
>[number];

export type OrganizationStatus = Organization["status"];

export type CreateOrganizationPayload = z.infer<
  typeof createOrganizationSchema
>;

export type UserCounts = {
  total: number;
  active: number;
  onboarding?: number;
};

interface GetLogoPresignedUrlParams {
  orgId: string;
  filename: string;
  contentType: string;
}

interface PresignedUrlResponse {
  uploadUrl: string;
  accessUrl: string;
}

// ----------------------------------------------------------------
// API Functions
// ----------------------------------------------------------------

// --- Basic CRUD Operations ---

/**
 * Fetch all organizations from the server.
 */
async function fetchOrganizations() {
  const headers = await authHeaders();
  const response = await organizationClient.index.$get({}, { headers });
  if (!response.ok) {
    throw new Error("Failed to fetch organizations");
  }
  return response.json();
}

/**
 * Fetch one organization by ID.
 */
async function fetchOrganizationById(id: string) {
  const headers = await authHeaders();
  const response = await organizationClient[":orgId"].$get(
    { param: { orgId: id } },
    { headers },
  );
  if (!response.ok) {
    throw new Error("Failed to fetch organization");
  }
  return response.json();
}

/**
 * Create a new organization.
 */
async function createOrganization(
  payload: z.infer<typeof createOrganizationSchema>,
) {
  const headers = await authHeaders();
  const response = await organizationClient.index.$post(
    { json: payload },
    { headers },
  );
  if (!response.ok) {
    throw new Error(`Failed to create organization: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Update an organization by ID.
 */
async function updateOrganization(
  id: string,
  payload: z.infer<typeof putOrganizationSchema>,
) {
  const headers = await authHeaders();
  const response = await organizationClient[":orgId"].$put(
    { param: { orgId: id }, json: payload },
    { headers },
  );
  if (!response.ok) {
    throw new Error(`Failed to update organization: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Delete an organization by ID.
 */
async function deleteOrganization(id: string) {
  const headers = await authHeaders();
  const response = await organizationClient[":orgId"].$delete(
    { param: { orgId: id } },
    { headers },
  );
  if (!response.ok) {
    throw new Error("Failed to delete organization");
  }
  return response.json();
}

// --- Patch Operations ---

/**
 * Patch organization responsible person.
 */
async function patchOrganizationResponsiblePerson({
  id,
  responsiblePerson,
  jiraTicket,
}: {
  id: string;
  responsiblePerson: string;
  jiraTicket?: string;
}) {
  const headers = await authHeaders();
  const response = await organizationClient[":orgId"][
    "responsible-person"
  ].$patch(
    {
      param: { orgId: id },
      json: {
        organization: {
          responsiblePerson,
          jiraTicket,
        },
      },
    },
    { headers },
  );
  if (!response.ok) throw new Error("Failed to patch responsible person");
  return response.json();
}

/**
 * Patch organization status.
 */
async function patchOrganizationStatus({
  id,
  status,
}: {
  id: string;
  status: OrganizationStatus;
}) {
  const headers = await authHeaders();
  const response = await organizationClient[":orgId"].status.$patch(
    {
      param: { orgId: id },
      json: {
        organization: {
          status,
        },
      },
    },
    { headers },
  );
  if (!response.ok) throw new Error("Failed to patch status");
  return response.json();
}

// --- Additional Data Fetching ---

/**
 * Get Logo Presigned URL
 */
async function getLogoPresignedUrl(
  params: GetLogoPresignedUrlParams,
): Promise<PresignedUrlResponse> {
  const headers = await authHeaders();
  const response = await organizationClient[":orgId"]["logo"][
    "presigned-url"
  ].$post(
    {
      param: { orgId: params.orgId },
      json: { filename: params.filename, contentType: params.contentType },
    },
    { headers },
  );
  if (!response.ok) {
    throw new Error("Failed to get pre-signed URL");
  }
  return response.json();
}

/**
 * Fetch user counts for an organization.
 */
async function fetchUserCounts(id: string): Promise<UserCounts> {
  const headers = await authHeaders();
  const response = await organizationClient[":orgId"]["user-counts"].$get(
    { param: { orgId: id } },
    { headers },
  );
  if (!response.ok) throw new Error("Failed to fetch user counts");
  const data = await response.json();
  return { ...data, onboarding: 0 };
}

/**
 * Fetch onboarding counts for all organizations.
 */
async function fetchAllOrganizationsOnboardingCounts() {
  const headers = await authHeaders();
  const response = await organizationClient["all"][
    "onboarding-user-counts"
  ].$get({}, { headers });
  if (!response.ok) throw new Error("Failed to fetch onboarding user counts");
  return response.json() as Promise<Record<string, number>>;
}

/**
 * Fetch users for an organization.
 */
async function fetchOrganizationUsers(id: string) {
  const headers = await authHeaders();
  const response = await organizationClient[":orgId"]["users"].$get(
    { param: { orgId: id } },
    { headers },
  );
  if (!response.ok) throw new Error("Failed to fetch organization users");
  return response.json();
}

// ----------------------------------------------------------------
// React Query Hooks
// ----------------------------------------------------------------

// --- Organization List ---

export const organizationsQueryOptions = queryOptions({
  queryKey: ["organizations"],
  queryFn: fetchOrganizations,
});

export function useOrganizations() {
  return useQuery(organizationsQueryOptions);
}

// --- Single Organization ---

export const organizationQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["organization", id],
    queryFn: () => fetchOrganizationById(id),
    enabled: Boolean(id),
  });

export function useOrganization(id: string) {
  return useQuery(organizationQueryOptions(id));
}

// --- Organization CRUD Operations ---

export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createOrganization,
    onSuccess: (newOrg) => {
      queryClient.setQueryData<Organization[]>(["organizations"], (old) =>
        old ? [...old, newOrg] : [newOrg],
      );
      queryClient.invalidateQueries({ queryKey: ["auditLogs"] });
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      data: Parameters<typeof updateOrganization>[1];
    }) => updateOrganization(vars.id, vars.data),
    onSuccess: (updatedOrg, variables) => {
      queryClient.setQueryData(["organization", variables.id], updatedOrg);
      queryClient.setQueryData<Organization[]>(["organizations"], (old) => {
        if (!old) return [updatedOrg];
        return old.map((org) => (org.id === variables.id ? updatedOrg : org));
      });
      queryClient.invalidateQueries({ queryKey: ["auditLogs"] });
    },
  });
}

export function useDeleteOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteOrganization(id),
    onSuccess: (_, id) => {
      queryClient.setQueryData(["organization", id], null);
      queryClient.setQueryData<Organization[]>(
        ["organizations"],
        (old) => old?.filter((org) => org.id !== id) ?? [],
      );
      queryClient.invalidateQueries({ queryKey: ["auditLogs"] });
    },
  });
}

// --- Organization Patch Operations ---

export function usePatchOrganizationResponsiblePerson() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      responsiblePerson: string;
      jiraTicket?: string;
    }) => patchOrganizationResponsiblePerson(vars),
    onSuccess: (updatedOrg, variables) => {
      queryClient.setQueryData(["organization", variables.id], updatedOrg);
      queryClient.setQueryData<Organization[]>(["organizations"], (old) => {
        if (!old) return [updatedOrg];
        return old.map((org) => (org.id === variables.id ? updatedOrg : org));
      });
      queryClient.invalidateQueries({ queryKey: ["auditLogs"] });
    },
  });
}

export function usePatchOrganizationStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; status: OrganizationStatus }) =>
      patchOrganizationStatus(vars),
    onSuccess: (updatedOrg, variables) => {
      queryClient.setQueryData(["organization", variables.id], updatedOrg);
      queryClient.setQueryData<Organization[]>(["organizations"], (old) => {
        if (!old) return [updatedOrg];
        return old.map((org) => (org.id === variables.id ? updatedOrg : org));
      });
      queryClient.invalidateQueries({ queryKey: ["auditLogs"] });
    },
  });
}

// --- Additional Data Hooks ---

export function useLogoPresignedUrl() {
  return useMutation<PresignedUrlResponse, Error, GetLogoPresignedUrlParams>({
    mutationFn: getLogoPresignedUrl,
  });
}

export const userCountsQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["organization", id, "user-counts"],
    queryFn: () => fetchUserCounts(id),
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
  });

export function useUserCounts(id: string) {
  return useQuery(userCountsQueryOptions(id));
}

export const allOrganizationsOnboardingCountsQueryOptions = queryOptions({
  queryKey: ["organizations", "onboarding-counts"],
  queryFn: () => fetchAllOrganizationsOnboardingCounts(),
  staleTime: 5 * 60 * 1000,
});

export function useAllOrganizationsOnboardingCounts() {
  return useQuery(allOrganizationsOnboardingCountsQueryOptions);
}

export const organizationUsersQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["organization", id, "users"],
    queryFn: () => fetchOrganizationUsers(id),
    enabled: Boolean(id),
  });

export function useOrganizationUsers(id: string) {
  return useQuery(organizationUsersQueryOptions(id));
}
